const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const getAuthHeader = () => {
  try {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (e) {
    return {};
  }
};

const handleResponse = async (response) => {
  if (!response.ok) {
    if (response.status === 401) {
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } catch (e) {
        // Ignore localStorage errors
      }
      window.location.href = '/login';
    }
    // Read error from response
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  // Return JSON for successful requests
  return response.json();
};

export const api = {
  // Auth
  login: async (email, password) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    return handleResponse(response);
  },

  getMe: async () => {
    const response = await fetch(`${API_URL}/api/auth/me`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  // Campaigns
  getCampaigns: async () => {
    const response = await fetch(`${API_URL}/api/campaigns`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  createCampaign: async (data) => {
    const response = await fetch(`${API_URL}/api/campaigns`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  getCampaign: async (id) => {
    const response = await fetch(`${API_URL}/api/campaigns/${id}`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  updateCampaign: async (id, data) => {
    const response = await fetch(`${API_URL}/api/campaigns/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  startCampaign: async (id) => {
    const response = await fetch(`${API_URL}/api/campaigns/${id}/start`, {
      method: 'POST',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  pauseCampaign: async (id) => {
    const response = await fetch(`${API_URL}/api/campaigns/${id}/pause`, {
      method: 'POST',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  stopCampaign: async (id) => {
    const response = await fetch(`${API_URL}/api/campaigns/${id}/stop`, {
      method: 'POST',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  deleteCampaign: async (id) => {
    const response = await fetch(`${API_URL}/api/campaigns/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  // Leads
  uploadLeads: (campaignId, file) => new Promise((resolve, reject) => {
    // Use XHR instead of fetch to avoid Response body consumption issues across all browsers
    const formData = new FormData();
    formData.append('file', file);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/campaigns/${campaignId}/leads/upload`);
    try {
      const token = localStorage.getItem('token');
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    } catch(e) {}
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data);
        } else {
          reject(new Error(data.detail || 'Upload failed'));
        }
      } catch(e) {
        reject(new Error('Upload failed — server error'));
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed — network error'));
    xhr.ontimeout = () => reject(new Error('Upload timed out'));
    xhr.timeout = 120000; // 2 minute timeout for large files
    xhr.send(formData);
  }),

  getLeads: async (campaignId, skip = 0, limit = 100) => {
    const response = await fetch(`${API_URL}/api/campaigns/${campaignId}/leads?skip=${skip}&limit=${limit}`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  // Audio
  uploadAudio: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/api/audio/upload`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: formData
    });
    return handleResponse(response);
  },

  getAudioFiles: async () => {
    const response = await fetch(`${API_URL}/api/audio`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  deleteAudio: async (id) => {
    const response = await fetch(`${API_URL}/api/audio/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  // Dashboard
  getDashboardStats: async () => {
    const response = await fetch(`${API_URL}/api/dashboard/stats`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  getBalance: async () => {
    const response = await fetch(`${API_URL}/api/balance`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  getRecentTransfers: async (limit = 15) => {
    const response = await fetch(`${API_URL}/api/recent-transfers?limit=${limit}`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  getCampaignResults: async (campaignId, skip = 0, limit = 100) => {
    const response = await fetch(`${API_URL}/api/campaigns/${campaignId}/results?skip=${skip}&limit=${limit}`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  exportCampaignResults: async (campaignId) => {
    const response = await fetch(`${API_URL}/api/campaigns/${campaignId}/export`, {
      headers: getAuthHeader()
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  },

  // Admin
  createTenant: async (data) => {
    const response = await fetch(`${API_URL}/api/admin/tenants`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  getTenants: async () => {
    const response = await fetch(`${API_URL}/api/admin/tenants`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  updateTenant: async (id, data) => {
    const response = await fetch(`${API_URL}/api/admin/tenants/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  deleteTenant: async (id) => {
    const response = await fetch(`${API_URL}/api/admin/tenants/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },


  // Test Call
  testCall: async (phone_number, campaign_id) => {
    const response = await fetch(`${API_URL}/api/test-call`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone_number, campaign_id })
    });
    return handleResponse(response);
  },

  // Settings
  changePassword: async (data) => {
    const response = await fetch(`${API_URL}/api/auth/change-password`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  }
};
